import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Segmented, Select, Space, Table, Tabs, Typography } from 'antd';
import FilterPanel from '../components/FilterPanel';
import api from '../api';

function fmtTime(seconds) {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtPct(v) {
  if (v == null) return '-';
  return `${v}%`;
}

const TREND_OPTS   = ['AHT', 'Talk Time', 'ACW', 'Calls'];
const TREND_KEY    = { AHT: 'AvgAHT', 'Talk Time': 'AvgAgentTime', ACW: 'AvgACWTime', Calls: 'CallCount' };
const TREND_IS_TIME = { AHT: true, 'Talk Time': true, ACW: true, Calls: false };

const BREAKDOWN_COLS = [
  { title: 'Agent Name',      dataIndex: 'Agent_Name',      key: 'Agent_Name',      width: 160, fixed: 'left', ellipsis: true, sorter: (a, b) => (a.Agent_Name ?? '').localeCompare(b.Agent_Name ?? '') },
  { title: 'Campaign',        dataIndex: 'Campaign',        key: 'Campaign',        width: 140, ellipsis: true, sorter: (a, b) => a.Campaign.localeCompare(b.Campaign) },
  { title: 'Media',           dataIndex: 'Media',           key: 'Media',           width: 110 },
  { title: 'Direction',       dataIndex: 'Direction',       key: 'Direction',       width: 90 },
  { title: 'Calls',           dataIndex: 'CallCount',       key: 'CallCount',       width: 70,  align: 'right', sorter: (a, b) => (a.CallCount ?? 0) - (b.CallCount ?? 0) },
  { title: 'Avg Talk',        dataIndex: 'AvgAgentTime',    key: 'AvgAgentTime',    width: 85,  align: 'right', render: fmtTime, sorter: (a, b) => (a.AvgAgentTime ?? 0) - (b.AvgAgentTime ?? 0) },
  { title: 'Avg ACW',         dataIndex: 'AvgACWTime',      key: 'AvgACWTime',      width: 80,  align: 'right', render: fmtTime, sorter: (a, b) => (a.AvgACWTime ?? 0) - (b.AvgACWTime ?? 0) },
  { title: 'Avg AHT',         dataIndex: 'AvgAHT',          key: 'AvgAHT',          width: 80,  align: 'right', render: fmtTime, sorter: (a, b) => (a.AvgAHT ?? 0) - (b.AvgAHT ?? 0) },
  { title: 'Called Support',  dataIndex: 'CalledSupportQty', key: 'CalledSupportQty', width: 115, align: 'right', sorter: (a, b) => (a.CalledSupportQty ?? 0) - (b.CalledSupportQty ?? 0) },
  { title: 'Called Tier 2',   dataIndex: 'CalledTier2Qty',   key: 'CalledTier2Qty',   width: 105, align: 'right', sorter: (a, b) => (a.CalledTier2Qty ?? 0) - (b.CalledTier2Qty ?? 0) },
];

const TALK_RANGE_COLS = [
  { title: 'Agent Name',     dataIndex: 'Agent_Name',   key: 'Agent_Name',   width: 160, fixed: 'left', ellipsis: true },
  { title: 'Talk Range',     dataIndex: 'TalkRange',    key: 'TalkRange',    width: 90 },
  { title: '% of Contacts',  dataIndex: 'PctOfContacts', key: 'PctOfContacts', width: 110, align: 'right', render: fmtPct },
  { title: 'Contacts',       dataIndex: 'Contacts',     key: 'Contacts',     width: 90,  align: 'right' },
  { title: 'Max Talk',       dataIndex: 'MaxTalk',      key: 'MaxTalk',      width: 90,  align: 'right', render: fmtTime },
];

const ACW_RANGE_COLS = [
  { title: 'Agent Name',     dataIndex: 'Agent_Name',   key: 'Agent_Name',   width: 160, fixed: 'left', ellipsis: true },
  { title: 'ACW Range',      dataIndex: 'ACWRange',     key: 'ACWRange',     width: 90 },
  { title: '% of Contacts',  dataIndex: 'PctOfContacts', key: 'PctOfContacts', width: 110, align: 'right', render: fmtPct },
  { title: 'Contacts',       dataIndex: 'Contacts',     key: 'Contacts',     width: 90,  align: 'right' },
  { title: 'Max ACW',        dataIndex: 'MaxACW',       key: 'MaxACW',       width: 90,  align: 'right', render: fmtTime },
];

function buildAgentMonthPivot(rows, metric) {
  if (!rows.length) return { cols: [], data: [] };

  const metricKey = TREND_KEY[metric];
  const isTime    = TREND_IS_TIME[metric];
  const fmt       = isTime ? fmtTime : (v) => (v ?? '-');

  const months = [...new Set(rows.map((r) => r.Month))].sort();
  const agents  = [...new Set(rows.map((r) => r.Agent_Name))].sort();
  const lookup  = {};
  rows.forEach((r) => { lookup[`${r.Agent_Name}||${r.Month}`] = r[metricKey]; });

  const cols = [
    { title: 'Agent Name', dataIndex: 'Agent_Name', key: 'Agent_Name', width: 160, fixed: 'left', ellipsis: true },
    ...months.map((m) => ({
      title: m, dataIndex: m, key: m, width: 90, align: 'right',
      render: (v) => fmt(v),
    })),
  ];

  const data = agents.map((agent) => {
    const row = { Agent_Name: agent };
    months.forEach((m) => { row[m] = lookup[`${agent}||${m}`] ?? null; });
    return row;
  });

  return { cols, data };
}

/**
 * `embedded` renders this page's content without its own filter panel or
 * title, driven by `filters` supplied from outside. The Campaign Summary uses
 * it so the campaign's tab and this page cannot disagree: there is one
 * implementation of these numbers, not two.
 */
export default function CampaignAgentStatsPage({ embedded = false, filters: embeddedFilters = null }) {
  const [breakdown,   setBreakdown]   = useState([]);
  const [agentMonth,  setAgentMonth]  = useState([]);
  const [talkRanges,  setTalkRanges]  = useState([]);
  const [acwRanges,   setAcwRanges]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [trendMetric, setTrendMetric] = useState('AHT');

  // Embedded only. Standalone this page has the filter panel, which offers the
  // same three and more; adding a second set of controls there would be two
  // places to filter the same table.
  //
  // Server-side, not client-side: the page has four tabs fed by four queries,
  // and filtering the loaded rows would narrow Summary while Trend, Talk Range
  // and ACW Range carried on showing everybody.
  const [fAgents,    setFAgents]    = useState([]);
  const [fDirection, setFDirection] = useState(null);
  const [fMedia,     setFMedia]     = useState([]);

  // Options come from an unfiltered load and are kept. Recomputing them from
  // filtered rows would let a filter erase its own options, so choosing one
  // agent would leave only that agent to choose from.
  const [options, setOptions] = useState({ agents: [], directions: [], media: [] });
  const anyFilter = fAgents.length > 0 || !!fDirection || fMedia.length > 0;

  const handleSearch = async (filters) => {
    setLoading(true);
    try {
      const res = await api.get('/api/campaign-agent-stats/call-stats', { params: filters });
      const rows = res.data.breakdown ?? [];
      if (!filters.agent_name && !filters.direction && !filters.media_name) {
        const uniq = (k) => [...new Set(rows.map((r) => r[k]).filter(Boolean))].sort();
        setOptions({
          agents:     uniq('Agent_Name'),
          directions: uniq('Direction'),
          media:      uniq('Media'),
        });
      }
      setBreakdown(rows);
      setAgentMonth(res.data.agent_month ?? []);
      setTalkRanges(res.data.talk_ranges ?? []);
      setAcwRanges(res.data.acw_ranges   ?? []);
    } catch {
      setBreakdown([]);
      setAgentMonth([]);
      setTalkRanges([]);
      setAcwRanges([]);
    } finally {
      setLoading(false);
    }
  };


  // Embedded: the filters come from the page above, so the search runs
  // whenever they change rather than when a button is pressed. Keyed on the
  // serialised filters because the object identity changes every render.
  const embeddedEffective = useMemo(() => {
    if (!embedded || !embeddedFilters) return null;
    return {
      ...embeddedFilters,
      ...(fAgents.length    ? { agent_name: fAgents.join(',') } : {}),
      ...(fDirection        ? { direction: fDirection }         : {}),
      ...(fMedia.length     ? { media_name: fMedia.join(',') }  : {}),
    };
  }, [embedded, embeddedFilters, fAgents, fDirection, fMedia]);

  const embeddedKey = embeddedEffective ? JSON.stringify(embeddedEffective) : null;
  useEffect(() => {
    if (!embeddedEffective) return;
    handleSearch(embeddedEffective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedKey]);

  const { cols: trendCols, data: trendData } = buildAgentMonthPivot(agentMonth, trendMetric);
  const emptyText = 'Select a campaign and click Search.';

  return (
    <>
      {!embedded && (
        <>
          <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
            Campaign Agent Stats
          </Typography.Title>

          <FilterPanel
            sessionKey="campaign-agent-stats"
            onSearch={handleSearch}
            loading={loading}
            requireCampaign
            defaultDateRange={[dayjs().subtract(1, 'month').startOf('month'), dayjs()]}
          />
        </>
      )}

      {embedded && (
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            placeholder="All agents"
            style={{ minWidth: 240 }}
            value={fAgents}
            onChange={setFAgents}
            options={options.agents.map((v) => ({ label: v, value: v }))}
          />
          <Select
            allowClear
            placeholder="All directions"
            style={{ width: 150 }}
            value={fDirection}
            onChange={(v) => setFDirection(v ?? null)}
            options={options.directions.map((v) => ({ label: v, value: v }))}
          />
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All media"
            style={{ minWidth: 180 }}
            value={fMedia}
            onChange={setFMedia}
            options={options.media.map((v) => ({ label: v, value: v }))}
          />
          {anyFilter && (
            <Button
              size="small"
              onClick={() => { setFAgents([]); setFDirection(null); setFMedia([]); }}
            >
              Clear
            </Button>
          )}
        </Space>
      )}

      <Tabs
        items={[
          {
            key: 'summary',
            label: 'Summary',
            children: (
              <Table
                rowKey={(r) => `${r.Agent_Name}-${r.Campaign}-${r.Media}-${r.Direction}`}
                dataSource={breakdown}
                columns={BREAKDOWN_COLS}
                loading={loading}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                locale={{ emptyText }}
              />
            ),
          },
          {
            key: 'trend',
            label: 'Agent Trend by Month',
            children: (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <Typography.Text strong>Agent Trend by Month</Typography.Text>
                  <Segmented
                    options={TREND_OPTS}
                    value={trendMetric}
                    onChange={setTrendMetric}
                    size="small"
                  />
                </div>
                <Table
                  rowKey="Agent_Name"
                  dataSource={trendData}
                  columns={trendCols}
                  loading={loading}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  pagination={false}
                  locale={{ emptyText }}
                />
              </>
            ),
          },
          {
            key: 'talk-range',
            label: 'Talk Time Range',
            children: (
              <Table
                rowKey={(r) => `${r.Agent_Name}-${r.TalkRange}`}
                dataSource={talkRanges}
                columns={TALK_RANGE_COLS}
                loading={loading}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                locale={{ emptyText }}
              />
            ),
          },
          {
            key: 'acw-range',
            label: 'ACW Range',
            children: (
              <Table
                rowKey={(r) => `${r.Agent_Name}-${r.ACWRange}`}
                dataSource={acwRanges}
                columns={ACW_RANGE_COLS}
                loading={loading}
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                locale={{ emptyText }}
              />
            ),
          },
        ]}
      />
    </>
  );
}
